/** Pure work-calendar primitives shared by browser and server. */
export const DEFAULT_WORK_TIME_ZONE = 'Europe/Tallinn';
export function validTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}
const formatters = new Map<string, Intl.DateTimeFormat>();
export function wallTime(
  instant: string | number | Date,
  timeZone = DEFAULT_WORK_TIME_ZONE,
): string {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, formatter);
  }
  const p = Object.fromEntries(
    formatter.formatToParts(new Date(instant)).map((v) => [v.type, v.value]),
  );
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
export function calendarDate(value: string, zone = DEFAULT_WORK_TIME_ZONE): string {
  if (!value || !Number.isFinite(Date.parse(value))) return '';
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
    ? wallTime(value, zone).slice(0, 10)
    : value.slice(0, 10);
}
export function nextCalendarDate(day: string, days = 1): string {
  return new Date(Date.parse(`${day}T12:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
}
/** Round-trip all offsets near the day. Ambiguous and nonexistent wall times require a decision. */
export function wallTimeToInstant(
  value: string,
  zone = DEFAULT_WORK_TIME_ZONE,
): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) || !validTimeZone(zone)) return undefined;
  const nominal = Date.parse(`${value}:00Z`);
  if (!Number.isFinite(nominal)) return undefined;
  const offsets = new Set<number>();
  for (const hours of [-48, -24, 0, 24, 48]) {
    const sample = nominal + hours * 3600000;
    offsets.add(Date.parse(`${wallTime(sample, zone)}:00Z`) - sample);
  }
  const matches = [...offsets]
    .map((offset) => new Date(nominal - offset).toISOString())
    .filter((iso) => wallTime(iso, zone) === value);
  return matches.length === 1 ? matches[0] : undefined;
}

export function validShift(shift: {
  dailyStart: string;
  dailyEnd: string;
  endDayOffset?: number;
  timeZone?: string;
  breakMinutes?: number;
  workingDays: number[];
}): boolean {
  const clock = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (!clock.test(shift.dailyStart) || !clock.test(shift.dailyEnd)) return false;
  const minute = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3));
  const duration =
    minute(shift.dailyEnd) + (shift.endDayOffset ?? 0) * 1440 - minute(shift.dailyStart);
  return (
    [0, 1].includes(shift.endDayOffset ?? 0) &&
    duration > 0 &&
    duration < 1440 &&
    Number.isInteger(shift.breakMinutes ?? 0) &&
    (shift.breakMinutes ?? 0) >= 0 &&
    (shift.breakMinutes ?? 0) < duration &&
    validTimeZone(shift.timeZone ?? DEFAULT_WORK_TIME_ZONE) &&
    shift.workingDays.length > 0 &&
    shift.workingDays.every((day) => Number.isInteger(day) && day >= 1 && day <= 7)
  );
}

/**
 * PB-04 — the one rule for "which local day/month does this belong to".
 * Instants stay UTC in storage; every day/month decision is made in the
 * site's IANA zone, never the device's or the server's.
 */

/** First valid IANA zone among the candidates (sheet snapshot → order → site); legacy = Tallinn. */
export function workTimeZone(...zones: Array<string | null | undefined>): string {
  for (const zone of zones) if (zone && validTimeZone(zone)) return zone;
  return DEFAULT_WORK_TIME_ZONE;
}

/** The order's site-local zone: snapshotted on the shift; legacy orders are Tallinn. */
export function jobTimeZone(
  job: { requirements?: { shift?: { timeZone?: string } | null } | null } | null | undefined,
): string {
  return workTimeZone(job?.requirements?.shift?.timeZone);
}

export interface WorkDateSubject {
  /** Start of the shift a continued sheet belongs to — a night shift is its start day's work. */
  shiftStartedAt?: string;
  entries?: Array<{ checkInAt?: string }>;
  submittedAt?: string;
  /** Zone snapshotted when the day started. */
  timeZone?: string;
}

/**
 * Local work date (YYYY-MM-DD) of a timesheet: the shift start, else the
 * earliest check-in, else the submit time — read in the sheet's own zone
 * snapshot, else `fallbackZone` (the order's), else Tallinn. A sheet with
 * nothing to go on returns ''.
 */
export function workDateOf(
  ts: WorkDateSubject | null | undefined,
  fallbackZone?: string | null,
): string {
  if (!ts) return '';
  const valid = (v: unknown): v is string =>
    typeof v === 'string' && Number.isFinite(Date.parse(v));
  const starts = (ts.entries ?? [])
    .map((e) => e?.checkInAt)
    .filter(valid)
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  const basis = [ts.shiftStartedAt, starts[0], ts.submittedAt].find(valid);
  return basis ? calendarDate(basis, workTimeZone(ts.timeZone, fallbackZone)) : '';
}

/** Local work month (YYYY-MM) of a timesheet; '' when undeterminable. */
export function workMonthOf(
  ts: WorkDateSubject | null | undefined,
  fallbackZone?: string | null,
): string {
  return workDateOf(ts, fallbackZone).slice(0, 7);
}

/** Today's local date (YYYY-MM-DD) in the zone, independent of the device timezone. */
export function localToday(zone?: string | null, now: string | number | Date = Date.now()): string {
  return wallTime(now, workTimeZone(zone)).slice(0, 10);
}

/**
 * The UTC instant a local calendar day starts at. Midnight is skipped by
 * no European zone, but where a zone's change happens at midnight the
 * first existing wall minute of the day is used.
 */
export function localDayStart(day: string, zone?: string | null): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const z = workTimeZone(zone);
  for (let minute = 0; minute <= 180; minute += 15) {
    const hh = String(Math.floor(minute / 60)).padStart(2, '0');
    const mm = String(minute % 60).padStart(2, '0');
    const exact = wallTimeToInstant(`${day}T${hh}:${mm}`, z);
    if (exact) return exact;
    // An ambiguous midnight (clock set back at 00:00): take the earlier occurrence.
    const nominal = Date.parse(`${day}T${hh}:${mm}:00Z`);
    const candidates = Array.from({ length: 29 }, (_, i) => nominal - (i - 14) * 3600000)
      .filter((t) => wallTime(t, z) === `${day}T${hh}:${mm}`)
      .sort((a, b) => a - b);
    if (candidates.length) return new Date(candidates[0]).toISOString();
  }
  return undefined;
}

/** [startAt, endAt) UTC instants of a local calendar month 'YYYY-MM'. */
export function localMonthRange(
  month: string,
  zone?: string | null,
): { startAt: string; endAt: string } | undefined {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return undefined;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return undefined;
  const next = mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`;
  const startAt = localDayStart(`${month}-01`, zone);
  const endAt = localDayStart(`${next}-01`, zone);
  return startAt && endAt ? { startAt, endAt } : undefined;
}

/** The current and previous local months ('YYYY-MM'), newest first. */
export function recentLocalMonths(
  count = 6,
  zone?: string | null,
  now: string | number | Date = Date.now(),
): string[] {
  const [y, m] = localToday(zone, now).split('-').map(Number);
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const idx = y * 12 + (m - 1) - i;
    out.push(`${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`);
  }
  return out;
}
